import { filter, isArray, replace } from 'lodash/fp';
import { isValidElement, ReactElement, ReactNode } from 'react';
import NextHead from 'next/head';

import ahmConstants from '../client/ahm-constants';

const findNodeContent = (elements: ReactNode[], type: string): ReactElement | undefined =>
  elements.find((element): element is ReactElement => isValidElement(element) && element.type === type);

// Remove title tag and description meta tag from array of elements
const stripTitleAndDescriptionNode = (elements: ReactNode[]): ReactNode[] =>
  filter(
    (element) =>
      !isValidElement(element) ||
      (element.type !== 'title' && !(element.type === 'meta' && element.props?.name === 'description')),
    elements,
  );

const findNodeByName = (elements: ReactNode[], name: string): ReactElement | undefined =>
  elements.find((element): element is ReactElement => isValidElement(element) && element.props?.name === name);

type HeadProps = {
  children: ReactNode;
  titleTemplate?: string;
};

const Head = ({ children, titleTemplate = ahmConstants.PAGE_TITLE_TEMPLATE }: HeadProps) => {
  const childrenArray = isArray(children) ? children : [children];

  const titleNode = findNodeContent(childrenArray, 'title');
  const expectedTitle = titleNode ? titleNode.props.children : 'Cheap private health insurance';
  const formattedTitle = titleTemplate ? replace('%s', expectedTitle, titleTemplate) : expectedTitle;

  // Ignore title and description nodes from children
  const remainingHeadNodes = stripTitleAndDescriptionNode(childrenArray);

  const descriptionNode = findNodeByName(childrenArray, 'description');
  const description = descriptionNode
    ? descriptionNode.props.content
    : 'Simple, smart and affordable health cover for the things you’ll actually use. Hospital, choosable extras and more. Join online.';

  return (
    <NextHead>
      <title>{formattedTitle}</title>
      <meta name="description" content={description} />
      <meta name="og:title" content={formattedTitle} />
      <meta name="og:description" content={description} />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      {remainingHeadNodes}
    </NextHead>
  );
};

export default Head;
